import { useField, useFormikContext } from 'formik';
import { parseInt } from 'lodash';
import moment from 'moment';
import { rgba } from 'polished';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';

import { CalendarIcon, CircleCheckmarkIcon, WarningIcon, flags } from '@treams/treams-icons';

import * as graphql from 'generated/graphql';

import { DatePicker } from './DatePicker';
import { Flag } from './Flag';
import { withFormik } from './Formik';
import { Popover } from './Popover';
import { Select } from './Select';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';

const Field = styled.input<{
	icon?: boolean;
	search?: boolean;
	disabled?: boolean;
	small?: boolean;
	valid?: boolean;
	error?: boolean;
	mergeWith?: 'top' | 'right' | 'bottom' | 'left';
	alternativeStyle?: 'enabled' | 'focused';
}>`
	border: none;
	padding: 10px;
	font-size: 16px;
	line-height: 20px;
	font-family: inherit;
	width: 100%;
	-webkit-appearance: none;
	appearance: none;
	background: white;

	&:focus {
		outline: none;
		background: linear-gradient(${(props) => props.theme.colors.lightBlueLight}, white);
		box-shadow:
			0px 0px 0px 1px ${(props) => props.theme.colors.primary},
			0px 0px 0px 3px ${(props) => rgba(props.theme.colors.primary, 0.25)},
			0px 1px 2px ${(props) => props.theme.colors.lightBlueDark} inset;
	}

	${(props) =>
		props.icon &&
		`
		padding-left: 39px;
	`};

	${(props) =>
		props.search
			? `
	background: ${rgba(props.theme.colors.lightBlue, 0.5)};
	border-radius: 24px;
	 `
			: `
	border-radius: 6px;
	box-shadow: 0px 0px 0px 1px ${
		props.valid
			? props.theme.colors.green
			: props.error
			? props.theme.colors.danger
			: props.theme.colors.lightBlue
	},
				0px 1px 2px ${rgba(props.theme.colors.lightBlue, 0.5)} inset;
	 `}

	${(props) =>
		props.mergeWith === 'right' &&
		`
			border-radius: 6px 0 0 6px;
			:focus { position: relative; z-index: 1; }
		`}
	 ${(props) => props.mergeWith === 'left' && 'border-radius: 0 6px 6px 0;'}
 

	${(props) =>
		props.small &&
		`
		line-height: 22px;
	 	font-size: 14px;
		padding: 6px 5px;

		${
			props.icon &&
			`
			padding-left: 24px;
		`
		}
	 `}

     ${(props) =>
		props.disabled &&
		props.alternativeStyle === undefined &&
		`
        background: ${props.theme.colors.lightBlueLight};
     `}


	${(props) =>
		props.alternativeStyle === 'focused' &&
		`
		outline: none;
		background: linear-gradient(${props.theme.colors.lightBlueLight}, white);
		box-shadow: 0px 0px 0px 1px ${props.theme.colors.primary},
			0px 0px 0px 3px ${rgba(props.theme.colors.primary, 0.25)},
			0px 1px 2px ${props.theme.colors.lightBlueDark} inset;
    `}

	 &::placeholder {
		color: ${(props) => props.theme.colors.slateBlue};
	}
`;

const Icon = styled(Flex)<{ small?: boolean; tinted?: boolean }>`
	position: absolute;
	top: 50%;
	left: 10px;
	margin-top: -12px;
	pointer-events: none;
	align-items: center;
	width: 24px;
	height: 24px;

	svg {
		${(props) =>
			props.tinted &&
			`path {
                fill: ${props.theme.colors.slateBlue};
            }`}
	}

	${(props) =>
		props.small &&
		`
		left: 4px;
		margin-top: -8px;
		width: 16px;
		height: 16px;
	`}
`;

const Suggestion = styled(Box)`
	position: absolute;
	top: 0px;
	left: 0px;
	padding: 10px;
	padding-left: 39px;
	pointer-events: none;
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 16px;
	line-height: 28px;
`;

const Hidden = styled.span`
	color: transparent;
`;

const SpinnerContainer = styled(Box)<{ small?: boolean }>`
	position: absolute;
	right: ${(props) => (props.small ? 8 : 20)}px;
	top: 50%;
	margin-top: -8px;
`;

const AccessoryContainer = styled(Box)<{ small?: boolean; valid?: boolean; error?: boolean }>`
	position: absolute;
	right: ${(props) => (props.small ? 8 : 10)}px;
	top: 50%;
	margin-top: -12px;
	svg {
		width: 24px;
		${(props) => props.valid && `fill: ${props.theme.colors.green}`};
		${(props) => props.error && `fill: ${props.theme.colors.danger}`};
	}
`;

interface IInputProps extends BoxProps {
	icon?: ReactNode;
	tintedIcon?: boolean;
	value?: string;
	loading?: boolean;
	valid?: boolean;
	error?: boolean;
	small?: boolean;
	search?: boolean;
	disabled?: boolean;
	focus?: boolean;
	type?: string;
	onFocus?: () => void;
	onBlur?: () => void;
	onChange: (value: string) => void;
	onKeyDown?: React.KeyboardEventHandler;
	completion?: string;
	autoFocus?: boolean;
	placeholder?: string;
	autoComplete?: string;
	mergeWith?: 'top' | 'right' | 'bottom' | 'left';
	alternativeStyle?: 'enabled' | 'focused';
}

export function Input(props: BoxProps & IInputProps) {
	const {
		icon,
		value,
		small,
		search,
		type,
		error,
		valid,
		onFocus,
		onBlur,
		onChange,
		onKeyDown,
		completion,
		autoFocus,
		placeholder,
		autoComplete,
		mergeWith,
		alternativeStyle,
		loading,
		...rest
	} = props;

	return (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		<Box {...(rest as any)} style={{ position: 'relative' }}>
			{icon && (
				<Icon small={small} tinted={props.tintedIcon ?? true}>
					{icon}
				</Icon>
			)}
			{loading && (
				<SpinnerContainer small={small}>
					<Spinner />
				</SpinnerContainer>
			)}
			{valid && (
				<AccessoryContainer small={small} valid>
					<CircleCheckmarkIcon />
				</AccessoryContainer>
			)}
			{error && (
				<AccessoryContainer small={small} error>
					<WarningIcon />
				</AccessoryContainer>
			)}
			<Field
				autoFocus={autoFocus}
				small={small}
				icon={!!icon}
				search={search}
				type={type}
				value={value}
				placeholder={placeholder}
				onFocus={onFocus}
				onBlur={onBlur}
				onKeyDown={onKeyDown}
				autoComplete={autoComplete}
				valid={valid}
				error={error}
				disabled={props.disabled}
				onChange={(event) => onChange(event.currentTarget.value)}
				mergeWith={mergeWith}
				alternativeStyle={alternativeStyle}
			/>
			{completion && completion.toLowerCase().startsWith((value ?? '').toLowerCase()) && (
				<Suggestion>
					<Hidden>{value}</Hidden>
					{completion.substring(value?.length ?? 0)}
				</Suggestion>
			)}
		</Box>
	);
}

export function NumericInput(
	props: BoxProps & IInputProps & { prefix?: string; onChange?: (value?: string) => void },
) {
	const intValue = parseInt(props.value ?? '');
	const valueString =
		props.value === undefined ? '' : isNaN(intValue) ? 0 : intValue.toLocaleString('nl-NL');
	const prefix = `${props.prefix ? `${props.prefix} ` : ''}`;

	return (
		<Box style={{ position: 'relative' }} flexGrow={1}>
			<Input
				{...props}
				value={`${prefix}${valueString}`}
				onChange={(value) => {
					const re = /^[0-9\b]+$/;
					const strippedValue = value.replace(prefix, '').replace(/[.]/g, '');

					if (strippedValue === '') {
						props.onChange(undefined);
						return;
					}

					if (re.test(strippedValue)) {
						props.onChange(strippedValue);
					}
				}}
			/>
		</Box>
	);
}

const FormikElement = withFormik(Input);

Input.Formik = function InputFormik(
	props: Omit<Omit<IInputProps, 'value'>, 'onChange'> & { validate?: boolean; name: string },
) {
	const form = useFormikContext();
	const [, meta] = useField(props.name);

	return (
		<FormikElement
			{...props}
			onKeyDown={(event) => {
				if (event.key === 'Enter') {
					form.submitForm();
					event.preventDefault();
					return;
				}
			}}
			error={props.validate && !!meta.error && meta.touched}
			valid={props.validate && !meta.error && meta.touched}
		/>
	);
};

const InputSkeleton = styled(Skeleton.Box)<{ search?: boolean }>`
	height: 40px;
	border-radius: ${(props) => (props.search ? 20 : 6)}px;
`;

Input.Skeleton = InputSkeleton;

function InputDatePicker(
	props: Omit<Omit<IInputProps, 'value'>, 'onChange'> & {
		value?: Date;
		onChange: (date?: Date) => void;
	},
) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(props.autoFocus ?? false);

	return (
		<Popover.Target
			visible={visible}
			contents={
				<DatePicker
					value={props.value}
					onChange={props.onChange}
					onDismiss={() => setVisible(false)}
				/>
			}
			onDismiss={() => setVisible(false)}
		>
			<Box tabIndex={0} onFocus={() => (props.disabled ? undefined : setVisible(true))}>
				<Input
					{...props}
					disabled
					icon={<CalendarIcon />}
					value={props.value ? moment(props.value).format('DD/MM/YYYY') : t('common.date.empty')}
					alternativeStyle={props.disabled ? undefined : visible ? 'focused' : 'enabled'}
					onChange={() => {}}
				/>
			</Box>
		</Popover.Target>
	);
}

Input.DatePicker = InputDatePicker;

const InputDatePickerFormikElement = withFormik(InputDatePicker);

function InputDatePickerFormik(
	props: Omit<Omit<IInputProps, 'value'>, 'onChange'> & { name: string; validate?: boolean },
) {
	const [, meta] = useField(props.name);

	return (
		<InputDatePickerFormikElement
			{...props}
			error={props.validate && !!meta.error && meta.touched}
			valid={props.validate && !meta.error && meta.touched}
		/>
	);
}

InputDatePicker.Formik = InputDatePickerFormik;

function InputLanguage(props: IInputProps) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);
	const [fetch, query] = graphql.useSupportedLanguageIDsLazyQuery();

	return (
		<Popover.Target
			visible={visible}
			contents={
				<Select.Menu
					tint={false}
					search={false}
					selectedValue={props.value}
					options={query.data?.viewer.workspace.supportedLanguageIDs.map((languageID) => ({
						icon: <Flag languageID={languageID as keyof typeof flags} />,
						title: t('common.languages.' + languageID),
						value: languageID,
					}))}
					onChange={(value) => {
						props.onChange(value!);
						setVisible(false);
					}}
				/>
			}
			onDismiss={() => setVisible(false)}
		>
			<Box
				tabIndex={0}
				onFocus={() => {
					if (props.disabled) {
						return;
					}

					setVisible(true);
					fetch();
				}}
			>
				<Input
					{...props}
					disabled
					tintedIcon={false}
					value={t('common.languages.' + props.value)}
					icon={<Flag languageID={props.value as keyof typeof flags} />}
					alternativeStyle={props.disabled ? undefined : visible ? 'focused' : 'enabled'}
					onChange={() => {}}
				/>
			</Box>
		</Popover.Target>
	);
}

Input.Language = InputLanguage;

const InputLanguageFormikElement = withFormik(InputLanguage);

function InputLanguageFormik(
	props: Omit<Omit<IInputProps, 'value'>, 'onChange'> & { name: string; validate?: boolean },
) {
	const [, meta] = useField(props.name);

	return (
		<InputLanguageFormikElement
			{...props}
			error={props.validate && !!meta.error && meta.touched}
			valid={props.validate && !meta.error && meta.touched}
		/>
	);
}

InputLanguage.Formik = InputLanguageFormik;
